import React from "react";
import { motion } from "framer-motion";
import { SHOP } from "./data";
import { euro, EASE } from "./primitives";

/**
 * Een bestelling als keukenbon. Vandaag komt die binnen in de app (met een melding);
 * een koppeling met een bonprinter staat op de planning.
 * order: { number, mode: "Afhalen" | "Levering", slot, items: [{ name, qty, price }], total, note }
 */
export default function KitchenTicket({ order, stamp = true, stampDelay = 0.75, className = "", compact = false }) {
   const pad = compact ? "px-4 pb-6 pt-4" : "px-5 pb-7 pt-5";
   return (
      <div className={`lp-ticket-bottom font-ticket text-lp-ink ${pad} ${className}`}>
         <div className="flex items-start justify-between text-[10px] uppercase tracking-[0.14em] text-lp-muted">
            <span>{SHOP.short}</span>
            <span>{order.time || "17:52"}</span>
         </div>
         <div className="mt-2 flex items-end justify-between">
            <p className={`${compact ? "text-xl" : "text-2xl"} font-bold tracking-tight`}>#{order.number}</p>
            <p className="rounded bg-lp-ink px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-lp-paper">
               {order.mode} {order.slot}
            </p>
         </div>
         <div className="my-3 border-t border-dashed border-lp-ink/30" />
         <ul className={`space-y-1.5 ${compact ? "text-[11px]" : "text-[12px]"}`}>
            {order.items.map((it) => (
               <li key={it.name} className="flex items-baseline">
                  <span className="w-7 font-bold">{it.qty}×</span>
                  <span className="truncate">{it.name}</span>
                  <span className="lp-leader" />
                  <span className="tabular-nums">{euro(it.price * it.qty)}</span>
               </li>
            ))}
            {order.deliveryCost ? (
               <li className="flex items-baseline text-lp-muted">
                  <span className="w-7" />
                  <span>Levering</span>
                  <span className="lp-leader" />
                  <span className="tabular-nums">{euro(order.deliveryCost)}</span>
               </li>
            ) : null}
         </ul>
         <div className="my-3 border-t border-dashed border-lp-ink/30" />
         <div className="flex items-baseline justify-between">
            <span className="text-[11px] uppercase tracking-[0.14em]">Totaal</span>
            <span className={`${compact ? "text-base" : "text-lg"} font-bold tabular-nums`}>{euro(order.total)}</span>
         </div>
         {/* streepjescode */}
         <div className="mt-4 flex items-center justify-between gap-3">
            <div className="flex h-7 items-stretch gap-[2px] opacity-80">
               {"31214122131141213212".split("").map((w, i) => (
                  <span key={i} className={i % 2 ? "bg-transparent" : "bg-lp-ink"} style={{ width: `${Number(w) * 1.5}px` }} />
               ))}
            </div>
            {stamp !== null && (
               <motion.span
                  initial={{ scale: 2.4, opacity: 0, rotate: -24 }}
                  animate={stamp ? { scale: 1, opacity: 1, rotate: -12 } : { scale: 2.4, opacity: 0, rotate: -24 }}
                  transition={stamp ? { delay: stampDelay, type: "spring", stiffness: 420, damping: 18 } : { duration: 0.2 }}
                  className="rounded-md border-2 border-lp-blue px-2 py-0.5 text-[13px] font-bold uppercase tracking-[0.16em] text-lp-blue mix-blend-multiply"
               >
                  Betaald
               </motion.span>
            )}
         </div>
      </div>
   );
}

/**
 * Kop met een sleuf waar de bon uit "rolt": beeld voor een binnenkomende bestelling.
 * `printing` + `children` (de bon) — de bon schuift van achter de sleuf naar beneden.
 */
export function TicketPrinter({ printing, children, label = "Nieuwe bestellingen", className = "", ticketKey }) {
   return (
      <div className={`relative ${className}`}>
         <div className="relative z-10 flex items-center justify-between rounded-2xl bg-lp-soft px-4 py-3 text-lp-paper shadow-[0_20px_40px_-20px_rgba(0,0,0,0.6)] ring-1 ring-white/5">
            <span className="font-ticket text-[10px] uppercase tracking-[0.16em] text-lp-paper/60">{label}</span>
            <span className="flex items-center gap-1.5">
               <span className={`h-1.5 w-1.5 rounded-full ${printing ? "animate-blink bg-emerald-400" : "bg-lp-paper/25"}`} />
               <span className="h-1.5 w-1.5 rounded-full bg-lp-paper/25" />
            </span>
            {/* sleuf */}
            <span className="absolute -bottom-1 left-3 right-3 h-2 rounded-full bg-black" />
         </div>
         <div className="relative -mt-1 overflow-hidden px-5 pb-6 [filter:drop-shadow(0_18px_18px_rgba(18,17,16,0.18))]">
            <motion.div
               key={ticketKey}
               initial={{ y: "-102%" }}
               animate={{ y: printing ? "0%" : "-102%" }}
               transition={{ duration: printing ? 1.6 : 0.5, ease: printing ? [0.45, 0, 0.2, 1] : EASE }}
            >
               {children}
            </motion.div>
         </div>
      </div>
   );
}
